import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { baseURL } from '../config';
import PageHeader from './reusable/PageHeader';

const ArrivedCustomersPage = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(false);
  const [morningCustomers, setMorningCustomers] = useState([]);
  const [eveningCustomers, setEveningCustomers] = useState([]);
  const [timePeriod, setTimePeriod] = useState('morning');
  const [dataLoadedDate, setDataLoadedDate] = useState(null);

  // Memoized fetch function to use in useEffect and button click
  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseURL}/arrived-customers?date=${selectedDate}`);
      if (response.status === 200) {
        const data = response.data;
        setMorningCustomers(data.morning || []);
        setEveningCustomers(data.evening || []);
        setDataLoadedDate(selectedDate);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // AUTO-FETCH: Runs on initial load
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const customersToDisplay = timePeriod === "morning" ? morningCustomers : eveningCustomers;
  const hasCustomers = customersToDisplay.length > 0;
  const isDataCurrent = dataLoadedDate === selectedDate;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 w-full">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <PageHeader title="Arrived Customers" />

        {/* Search & Stats Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
          <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            
            {/* Select Date */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Arrival Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none font-medium text-gray-700"
              />
            </div>

            {/* Time Period Toggle */}
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Shift Selection
              </label>
              <div className="flex p-1 bg-gray-100 rounded-xl">
                <button
                  onClick={() => setTimePeriod("morning")}
                  className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${
                    timePeriod === "morning"
                      ? "bg-white text-indigo-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Morning
                </button>
                <button
                  onClick={() => setTimePeriod("evening")}
                  className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${
                    timePeriod === "evening"
                      ? "bg-white text-indigo-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Evening
                </button>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex gap-3">
              <button
                onClick={fetchCustomers}
                disabled={loading}
                className={`flex-1 px-6 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl shadow-lg shadow-indigo-100 transition-all active:scale-95 ${
                  loading ? "opacity-70 cursor-not-allowed" : "hover:bg-indigo-700"
                }`}
              >
                {loading ? "Syncing..." : "Refresh List"}
              </button>
            </div>
          </div>

          {/* Quick Summary Bar */}
          <div className="bg-indigo-50 px-6 py-3 flex justify-between items-center border-t border-indigo-100">
            <p className="text-sm text-indigo-700 font-medium">
              Showing <span className="font-bold">{customersToDisplay.length}</span> arrivals for {timePeriod}
            </p>
            {!isDataCurrent && (
              <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold uppercase tracking-tight">
                Date changed - Update list
              </span>
            )}
          </div>
        </div>

        {/* Customer Cards Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             {[1,2,3].map(i => (
               <div key={i} className="h-40 bg-gray-200 animate-pulse rounded-2xl"></div>
             ))}
          </div>
        ) : hasCustomers ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {customersToDisplay.map((customer, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold">
                      {customer.name.charAt(0).toUpperCase()}
                    </div>
                    <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full ${
                      timePeriod === "morning" ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"
                    }`}>
                      {timePeriod}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-gray-800 capitalize mb-1 group-hover:text-indigo-600 transition-colors">
                    {customer.name}
                  </h3>
                  <p className="text-xs font-medium text-gray-400 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    {customer.date}
                  </p>

                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">Weight</p>
                      <p className="text-lg font-black text-gray-700 mt-1">
                        {customer.weight} <span className="text-xs font-medium">KG</span>
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <p className="text-[10px] text-gray-400 font-bold uppercase">Fat %</p>
                      <p className="text-lg font-black text-gray-700 mt-1">
                        {customer.fat}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="bg-white rounded-3xl border-2 border-dashed border-gray-100 p-12 text-center">
            <div className="bg-gray-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
               <svg className="h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">No arrivals today</h3>
            <p className="text-gray-500 text-sm mt-2 max-w-xs mx-auto">
              We couldn't find any customers recorded for this date and shift.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArrivedCustomersPage;